"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Star } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { useEffect, useState } from "react";
export default function CustomerReviewCarousel() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: collageRef,
    isVisible: collageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const testimonials = [{
    name: "Alex R.",
    tag: "TECH ENTHUSIAST",
    quote: "VPNGENIX provides the most reliable and fastest connection I have ever experienced for my remote work.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/user_alex_vpngenix.webp",
    width: "w-[340px]",
    rotate: "-rotate-3",
    top: "top-0",
    left: "left-0",
    z: "z-30",
    tag2: "VERIFIED USER"
  }, {
    name: "Sarah M.",
    tag: "PRIVACY ADVOCATE",
    quote: "The interface is incredibly intuitive, and I feel completely secure knowing my data is encrypted.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/user_sarah_vpngenix.webp",
    width: "w-[300px]",
    rotate: "rotate-2",
    top: "top-8",
    left: "left-[300px]",
    z: "z-20",
    tag2: ""
  }, {
    name: "Marcus T.",
    tag: "GAMING PRO",
    quote: "Zero lag during my gaming sessions. VPNGENIX is truly a game-changer for online performance.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/user_marcus_vpngenix.webp",
    width: "w-[280px]",
    rotate: "-rotate-2",
    top: "top-[230px]",
    left: "left-[60px]",
    z: "z-10",
    tag2: "PREMIUM MEMBER"
  }, {
    name: "Elena K.",
    tag: "GLOBAL TRAVELER",
    quote: "Accessing my favorite content from anywhere in the world has never been easier. Highly recommended.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/user_elena_vpngenix.png",
    width: "w-[320px]",
    rotate: "rotate-3",
    top: "top-[260px]",
    left: "left-[360px]",
    z: "z-20",
    tag2: ""
  }, {
    name: "David J.",
    tag: "SECURITY EXPERT",
    quote: "The robust security protocols and no-logs policy make VPNGENIX the gold standard for digital privacy.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/user_david_vpngenix.webp",
    width: "w-[290px]",
    rotate: "-rotate-1",
    top: "top-[460px]",
    left: "left-[220px]",
    z: "z-10",
    tag2: "LONG-TERM USER"
  }];
  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => setCurrent(api.selectedScrollSnap()));
  }, [api]);
  return <section data-section-id="1017" id="customerreviewcarousel" className="relative bg-background text-foreground py-24 md:py-32 overflow-hidden">
      {}
      <div className="absolute top-0 left-0 right-0 -translate-y-px pointer-events-none">
        <svg viewBox="0 0 1440 64" preserveAspectRatio="none" className="w-full h-8 md:h-16" aria-hidden="true">
          <path d="M0,32 C180,8 320,56 540,32 C760,8 900,56 1080,32 C1260,8 1340,48 1440,28 L1440,0 L0,0 Z" fill="var(--primary)" />
        </svg>
      </div>

      {}
      <div className="absolute -left-24 top-1/3 w-80 h-80 opacity-20 pointer-events-none" style={{
      background: "var(--secondary)",
      borderRadius: "62% 38% 47% 53% / 45% 60% 40% 55%"
    }} aria-hidden="true" />
      <div className="absolute -right-32 bottom-10 w-96 h-96 opacity-15 pointer-events-none" style={{
      background: "var(--primary)",
      borderRadius: "58% 42% 63% 37% / 52% 38% 62% 48%"
    }} aria-hidden="true" />

      <div className="container mx-auto max-w-[1320px] px-6 md:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          {}
          <div ref={headerRef} className={`lg:col-span-4 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="lg:sticky lg:top-24">
              {}
              <div className="flex items-center gap-3 mb-8">
                <span className="inline-flex items-center justify-center">
                  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                    <circle cx="7" cy="7" r="4" fill="var(--primary)" />
                    <path d="M11 4 Q14 2 13 0" stroke="var(--primary)" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                  </svg>
                </span>
                <span className="font-default text-xs md:text-sm font-medium tracking-[0.16em] uppercase text-foreground">
                  &#x25D0; 05 &mdash; User Feedback
                </span>
              </div>

              {}
              <h2 className="font-default font-bold tracking-tight text-foreground leading-[1.0] mb-6" style={{
              fontSize: "clamp(2.25rem, 5vw, 4.5rem)"
            }}>
                Trusted by our{" "}
                <span className="relative inline-block">
                  <svg className="absolute -inset-x-2 inset-y-0 w-[calc(100%+1rem)] h-full -z-0 pointer-events-none" viewBox="0 0 200 60" preserveAspectRatio="none" aria-hidden="true">
                    <path d="M8,40 Q40,12 80,32 T160,30 Q185,32 192,42" stroke="var(--primary)" strokeWidth="14" fill="none" strokeLinecap="round" opacity="0.85" style={{
                    strokeDasharray: 400,
                    strokeDashoffset: headerVisible ? 0 : 400,
                    transition: "stroke-dashoffset 900ms ease-out 200ms"
                  }} />
                  </svg>
                  <span className="relative font-default">community</span>
                </span>
              </h2>

              <p className="font-default text-muted-foreground leading-[1.55] mb-8" style={{
              fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)"
            }}>
                Join thousands of satisfied users who rely on VPNGENIX for secure, fast, and private internet access every single day.
              </p>

              {}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  {[0, 1, 2, 3, 4].map(i => <Star key={i} data-index={i} className="w-5 h-5 fill-[var(--primary)] text-[var(--primary)]" strokeWidth={1.5} />)}
                </div>
                <span className="font-default text-sm font-medium tracking-wider text-foreground">
                  4.9 &middot; 5,000+ reviews
                </span>
              </div>

              {}
              <div className="mt-8 flex items-center gap-2">
                <div className="h-px flex-1 bg-border" />
                <svg width="12" height="14" viewBox="0 0 12 14" aria-hidden="true">
                  <circle cx="6" cy="9" r="3" fill="var(--primary)" />
                  <path d="M9 7 Q11 4 10 1" stroke="var(--border)" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                </svg>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-8" ref={collageRef}>
            {}
            <div className="hidden lg:block relative h-[760px]">
              {testimonials.map((t, i) => <div key={i} data-index={i} className={`group absolute ${t.top} ${t.left} ${t.width} ${t.z} ${t.rotate} transition-all duration-[280ms] ease-out hover:rotate-0 hover:-translate-y-1`} style={{
              opacity: collageVisible ? 1 : 0,
              transform: collageVisible ? undefined : "translateY(24px)",
              transitionProperty: "opacity, transform, rotate",
              transitionDuration: "700ms",
              transitionDelay: `${i * 90}ms`
            }}>
                  <div className="relative bg-card border border-border p-7 shadow-md" style={{
                borderRadius: "var(--radius-lg)"
              }}>
                    {}
                    {t.tag2 && <div className="absolute -top-4 -right-3 bg-primary text-primary-foreground px-3 py-1 -rotate-[6deg] shadow-sm" style={{
                  borderRadius: "var(--radius-md)"
                }}>
                        <span className="font-default text-[10px] font-medium tracking-[0.12em] uppercase whitespace-nowrap">
                          {t.tag2}
                        </span>
                      </div>}

                    {}
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-14 h-14 overflow-hidden border border-border shrink-0" style={{
                    borderRadius: "var(--radius-lg)"
                  }}>
                        <SafeHydrate><Avatar className="w-full h-full">
                          <AvatarImage src={t.image} alt={t.name} />
                          <AvatarFallback className="bg-accent text-accent-foreground font-default font-bold">
                            {t.name.charAt(0)}
                          </AvatarFallback>
                        </Avatar></SafeHydrate>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-default font-bold text-foreground leading-tight">
                          {t.name}
                        </h3>
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className="font-default text-[10px] font-medium tracking-[0.12em] uppercase text-muted-foreground">
                            &middot; {t.tag}
                          </span>
                        </div>
                      </div>
                    </div>

                    <p className="font-default text-foreground leading-[1.5] text-[1.0625rem]">
                      &ldquo;{t.quote}&rdquo;
                    </p>
                  </div>
                </div>)}
            </div>

            {}
            <div className="lg:hidden">
              <Carousel opts={{
              align: "center",
              loop: true
            }} setApi={setApi} className="w-full">
                <CarouselContent>
                  {testimonials.map((t, i) => <CarouselItem key={i} data-index={i} className="basis-[85%]">
                      <div className="relative bg-card border border-border p-6 mx-1 shadow-md" style={{
                    borderRadius: "var(--radius-lg)"
                  }}>
                        {t.tag2 && <div className="absolute -top-3 -right-2 bg-primary text-primary-foreground px-2.5 py-1 -rotate-[6deg]" style={{
                      borderRadius: "var(--radius-md)"
                    }}>
                            <span className="font-default text-[10px] font-medium tracking-[0.12em] uppercase whitespace-nowrap">
                              {t.tag2}
                            </span>
                          </div>}

                        <div className="flex items-start gap-3 mb-4">
                          <div className="w-12 h-12 overflow-hidden border border-border shrink-0" style={{
                        borderRadius: "var(--radius-lg)"
                      }}>
                            <SafeHydrate><Avatar className="w-full h-full">
                              <AvatarImage src={t.image} alt={t.name} />
                              <AvatarFallback className="bg-accent text-accent-foreground font-default font-bold">
                                {t.name.charAt(0)}
                              </AvatarFallback>
                            </Avatar></SafeHydrate>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-default font-bold text-foreground leading-tight">
                              {t.name}
                            </h3>
                            <span className="font-default text-[10px] font-medium tracking-[0.12em] uppercase text-muted-foreground mt-1 inline-block">
                              &middot; {t.tag}
                            </span>
                          </div>
                        </div>

                        <p className="font-default text-foreground leading-[1.5] text-base">
                          &ldquo;{t.quote}&rdquo;
                        </p>
                      </div>
                    </CarouselItem>)}
                </CarouselContent>

                {}
                <div className="flex items-center justify-center gap-3 mt-8">
                  {testimonials.map((_, i) => <button key={i} data-index={i} onClick={() => api?.scrollTo(i)} aria-label={`Go to testimonial ${i + 1}`} className="relative flex items-center justify-center">
                      <svg width="16" height="18" viewBox="0 0 16 18" aria-hidden="true">
                        <circle cx="8" cy="11" r={current === i ? 5 : 3} fill={current === i ? "var(--primary)" : "var(--border)"} style={{
                      transition: "all 240ms ease"
                    }} />
                        {current === i && <path d="M12 8 Q15 5 14 1" stroke="var(--primary)" strokeWidth="1.5" fill="none" strokeLinecap="round" />}
                      </svg>
                    </button>)}
                </div>
              </Carousel>
            </div>
          </div>
        </div>
      </div>
    </section>;
}